/**
 * EmployerLookupPage (FND-021, PAC-020).
 *
 * Fuzzy employer search with type-ahead auto-suggest (GET /employers/search),
 * an employer detail panel (GET /employers/{id} + contacts), and a benefit-
 * package recency indicator so advocates can see at a glance whether an
 * employer's benefit data is fresh before relying on it for a match.
 */
import { useState } from 'react';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { PageHeader } from '../components/PageHeader';
import {
  useEmployerDetail,
  useEmployerSearch,
  type EmployerMatch,
} from '../hooks/useEmployers';
import type { BenefitPackage } from '../api/types';

/** Recency thresholds (days) for the benefit-package freshness badge. */
const RECENCY_FRESH_DAYS = 90;
const RECENCY_STALE_DAYS = 365;

interface Recency {
  label: string;
  color: string;
  background: string;
}

/** Classify benefit-package recency from `recency_validated_at`. */
function classifyRecency(pkg?: BenefitPackage | null): Recency {
  const validatedAt = pkg?.recency_validated_at ?? null;
  if (!validatedAt) {
    return { label: 'Never validated', color: '#9A3412', background: '#FFEDD5' };
  }
  const days = Math.floor((Date.now() - new Date(validatedAt).getTime()) / 86_400_000);
  if (Number.isNaN(days)) {
    return { label: 'Unknown', color: 'var(--text-secondary)', background: 'var(--surface)' };
  }
  if (days <= RECENCY_FRESH_DAYS) {
    return { label: `Validated ${days}d ago`, color: '#166534', background: '#DCFCE7' };
  }
  if (days <= RECENCY_STALE_DAYS) {
    return { label: `Aging — ${days}d ago`, color: '#92400E', background: '#FEF3C7' };
  }
  return { label: `Stale — ${days}d ago`, color: '#991B1B', background: '#FEE2E2' };
}

function RecencyBadge({ pkg }: { pkg?: BenefitPackage | null }) {
  const r = classifyRecency(pkg);
  return (
    <span style={{ ...styles.badge, color: r.color, background: r.background }}>
      {r.label}
      {pkg?.source ? ` · ${pkg.source}` : ''}
    </span>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  return <span style={styles.scoreBadge}>{pct}% match</span>;
}

export function EmployerLookupPage() {
  const { query, setQuery, suggestions, isSearching, error } = useEmployerSearch();
  const detail = useEmployerDetail();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = (match: EmployerMatch) => {
    setSelectedId(match.employer.employer_id);
    detail.load(match.employer.employer_id);
  };

  return (
    <>
      <PageHeader
        title="Employer Lookup"
        subtitle="Fuzzy search for an employer to verify benefits and ESI eligibility (PAC-020)."
      />

      <div style={styles.layout}>
        {/* Search + suggestions column */}
        <section style={styles.searchCol}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Start typing an employer name…"
            style={styles.searchInput}
            aria-label="Employer search"
            autoComplete="off"
          />
          {isSearching && <LoadingIndicator text="Searching…" />}
          {error && (
            <p style={styles.error} role="alert">
              {error.message}
            </p>
          )}
          {!isSearching && query.trim() && suggestions.length === 0 && !error && (
            <p style={styles.empty}>No employers match “{query.trim()}”.</p>
          )}
          <ul style={styles.suggestions}>
            {suggestions.map((match) => {
              const isActive = match.employer.employer_id === selectedId;
              return (
                <li key={match.employer.employer_id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(match)}
                    style={{
                      ...styles.suggestion,
                      ...(isActive ? styles.suggestionActive : {}),
                    }}
                  >
                    <span style={styles.suggestionName}>{match.employer.name}</span>
                    <ScoreBadge score={match.score} />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Detail column */}
        <section style={styles.detailCol}>
          {detail.isLoading && <LoadingIndicator text="Loading employer…" />}
          {detail.error && (
            <p style={styles.error} role="alert">
              {detail.error.message}
            </p>
          )}
          {!detail.isLoading && !detail.employer && !detail.error && (
            <p style={styles.empty}>Select an employer to view details.</p>
          )}
          {detail.employer && (
            <article style={styles.card}>
              <header style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>{detail.employer.name}</h2>
                <RecencyBadge pkg={detail.employer.benefit_package} />
              </header>

              <dl style={styles.detailGrid}>
                <dt style={styles.dt}>Employer ID</dt>
                <dd style={styles.dd}>{detail.employer.employer_id}</dd>
                <dt style={styles.dt}>ESI available</dt>
                <dd style={styles.dd}>
                  {detail.employer.esi_available === undefined
                    ? '—'
                    : detail.employer.esi_available
                      ? 'Yes'
                      : 'No'}
                </dd>
                <dt style={styles.dt}>Preferred enrollment</dt>
                <dd style={styles.dd}>
                  {detail.employer.preferred_enrollment_method ?? '—'}
                </dd>
              </dl>

              <h3 style={styles.sectionLabel}>Benefit package recency</h3>
              {detail.employer.benefit_package ? (
                <p style={styles.recencyLine}>
                  Last validated:{' '}
                  <strong>
                    {detail.employer.benefit_package.recency_validated_at ?? 'never'}
                  </strong>
                  {detail.employer.benefit_package.source
                    ? ` (source: ${detail.employer.benefit_package.source})`
                    : ''}
                </p>
              ) : (
                <p style={styles.empty}>No benefit package on file.</p>
              )}

              <h3 style={styles.sectionLabel}>Contacts</h3>
              {detail.contacts.length === 0 ? (
                <p style={styles.empty}>No contacts on file.</p>
              ) : (
                <ul style={styles.contactList}>
                  {detail.contacts.map((c, i) => (
                    <li key={`${c.name}-${i}`} style={styles.contactItem}>
                      <span style={styles.contactName}>{c.name}</span>
                      {c.role ? <span style={styles.contactRole}>{c.role}</span> : null}
                      <span style={styles.contactMeta}>
                        {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          )}
        </section>
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 24, maxWidth: 1080, margin: '0 auto', width: '100%' },
  h1: { margin: '0 0 4px', fontSize: '1.5rem' },
  subtitle: { margin: '0 0 20px', color: 'var(--text-secondary)' },
  layout: { display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 24 },
  searchCol: { display: 'flex', flexDirection: 'column', gap: 8 },
  searchInput: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: '1px solid var(--border)',
    borderRadius: 6,
  },
  suggestions: { listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  suggestion: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    padding: '10px 12px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'left',
  },
  suggestionActive: { borderColor: 'var(--primary)', background: 'var(--primary-light)' },
  suggestionName: { fontWeight: 500, color: 'var(--text-primary)' },
  detailCol: { minHeight: 200 },
  card: {
    background: '#FFFFFF',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: 20,
    boxShadow: 'var(--shadow-sm)',
  },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
  cardTitle: { margin: 0, fontSize: '1.2rem' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 16, rowGap: 6, margin: '0 0 16px' },
  dt: { color: 'var(--text-secondary)', fontSize: '0.86rem' },
  dd: { margin: 0, fontWeight: 500 },
  sectionLabel: { fontSize: '0.86rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)', margin: '12px 0 6px' },
  recencyLine: { margin: '0 0 4px' },
  contactList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  contactItem: { display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 0', borderBottom: '1px solid var(--border)' },
  contactName: { fontWeight: 500 },
  contactRole: { fontSize: '0.82rem', color: 'var(--text-secondary)' },
  contactMeta: { fontSize: '0.86rem', color: 'var(--text-secondary)' },
  badge: { fontSize: '0.76rem', fontWeight: 600, padding: '3px 10px', borderRadius: 12, whiteSpace: 'nowrap' },
  scoreBadge: { fontSize: '0.74rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--primary-light)', color: 'var(--primary-dark)', whiteSpace: 'nowrap' },
  error: { color: 'var(--danger, #B91C1C)', fontSize: '0.9rem' },
  empty: { color: 'var(--text-secondary)', fontSize: '0.9rem' },
};
